import type { ExportOptions, ExportPreview, JournalLine, UnmappedItem } from './exportTypes';
import { sumCents } from './money';

/** The exact integer-cents totals of the lines shown, to cross-check what the server reported. */
export function journalTotals(lines: readonly JournalLine[]): { debitsCents: number; creditsCents: number } {
  return {
    debitsCents: sumCents(lines.filter((l) => l.side === 'Debit').map((l) => l.amountCents)),
    creditsCents: sumCents(lines.filter((l) => l.side === 'Credit').map((l) => l.amountCents)),
  };
}

/** Do the totals the server reported match the lines it sent, to the cent? A mismatch means the response cannot be trusted for an export. */
export function totalsAgree(preview: ExportPreview): boolean {
  if (!preview.journal) return false;
  const { debitsCents, creditsCents } = journalTotals(preview.journal.lines);
  const j = preview.journal;
  return debitsCents === j.totalDebitsCents && creditsCents === j.totalCreditsCents && j.differenceCents === debitsCents - creditsCents;
}

/**
 * Whether Export CSV may be offered: the SERVER preview is Ready with no blockers, the journal balances exactly, the totals shown add up from the
 * lines in integer cents, and the reconciliation is complete. The server still re-validates when the export is generated.
 */
export function previewIsExportable(preview: ExportPreview | null): boolean {
  if (!preview || preview.status !== 'Ready' || preview.blockers.length > 0 || !preview.journal) return false;
  return preview.journal.isBalanced && totalsAgree(preview) && preview.reconciliation?.isComplete !== false;
}

export const fixMappingUrl = (runId: string, item: Pick<UnmappedItem, 'category' | 'subcategory' | 'payrollCode'>): string => {
  const q = new URLSearchParams({ tab: 'gl-mapping', runId, category: item.category, add: '1' });
  if (item.subcategory) q.set('subcategory', item.subcategory);
  if (item.payrollCode) q.set('payrollCode', item.payrollCode);
  return `/accounting?${q.toString()}`;
};

/** Where to go to fix a blocker the mapping options add (null when the fix is not a screen in this module). */
export function blockerLink(code: string, runId: string, options: ExportOptions): { to: string; label: string } | null {
  const run = encodeURIComponent(runId);
  switch (code) {
    case 'GroupMappingMissing':
      return { to: `/accounting?tab=gl-mapping&view=by-group&runId=${run}${options.wageGroupDimension ? `&dimension=${options.wageGroupDimension}` : ''}`, label: 'Assign accounts to groups' };
    case 'GroupMembershipMissing':
    case 'EmployeeGroupAmbiguous':
      return options.wageGroupDimension === 'EmployeeGroup' ? { to: '/accounting?tab=gl-mapping&view=employee-groups', label: 'Manage employee groups' } : null;
    case 'DefaultWageAccountRequired':
      return { to: fixMappingUrl(runId, { category: 'EarningsGross', subcategory: null, payrollCode: null }), label: 'Add the default wage mapping' };
    case 'NetPayNotConsolidatable':
      return { to: `/accounting?tab=gl-mapping&runId=${run}`, label: 'Review the net pay mappings' };
    case 'CodesWithoutMapping':
      return { to: '/accounting?tab=code-mappings', label: 'Open Code mappings' };
    default:
      return null;
  }
}
